import React, { useState, useEffect } from 'react';
import {
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  Checkbox,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  DragIndicator as DragIcon,
  ColorLens as ColorIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { IBadge, ICategory } from '../../types/damTypes';
import { getBadges, createBadge, updateBadge, deleteBadge, reorderBadges, BadgeCreateData, BadgeUpdateData } from '../../services/badgeService';

interface BadgeManagementPageProps {
  categories: ICategory[];
  loading: boolean;
  error: string | null;
  onBadgeUpdated: () => void;
}

interface EditingBadge {
  id: string | null;
  name: string;
  color: string;
  textColor: string;
  displayOrder: number;
  visibleInAll: boolean;
  visibleInCategories: string[];
  displayEmoji: string;
  showName: boolean;
  iconUrl: string;
}

// Predefined color options
const PRESET_COLORS = [
  { name: 'Blue', color: '#2196F3', textColor: '#FFFFFF' },
  { name: 'Red', color: '#F44336', textColor: '#FFFFFF' },
  { name: 'Green', color: '#4CAF50', textColor: '#FFFFFF' },
  { name: 'Orange', color: '#FF9800', textColor: '#FFFFFF' },
  { name: 'Purple', color: '#9C27B0', textColor: '#FFFFFF' },
  { name: 'Teal', color: '#009688', textColor: '#FFFFFF' },
  { name: 'Pink', color: '#E91E63', textColor: '#FFFFFF' },
  { name: 'Brown', color: '#795548', textColor: '#FFFFFF' },
  { name: 'Gray', color: '#607D8B', textColor: '#FFFFFF' },
  { name: 'Light Blue', color: '#03A9F4', textColor: '#FFFFFF' },
  { name: 'Light Green', color: '#8BC34A', textColor: '#000000' },
  { name: 'Amber', color: '#FFC107', textColor: '#000000' },
  { name: 'Deep Orange', color: '#FF5722', textColor: '#FFFFFF' },
  { name: 'Indigo', color: '#3F51B5', textColor: '#FFFFFF' },
  { name: 'Cyan', color: '#00BCD4', textColor: '#FFFFFF' },
  { name: 'Lime', color: '#CDDC39', textColor: '#000000' },
  { name: 'Deep Purple', color: '#673AB7', textColor: '#FFFFFF' },
  { name: 'Yellow', color: '#FFEB3B', textColor: '#000000' },
  { name: 'Black', color: '#000000', textColor: '#FFFFFF' },
  { name: 'White', color: '#FFFFFF', textColor: '#000000' },
  // Extra presets
  { name: 'Burgundy', color: '#800020', textColor: '#FFFFFF' },
  { name: 'Olive', color: '#556B2F', textColor: '#FFFFFF' },
  { name: 'Navy', color: '#001f3f', textColor: '#FFFFFF' },
  { name: 'Gold', color: '#C9B037', textColor: '#000000' },
  { name: 'Silver', color: '#C0C0C0', textColor: '#000000' }
];

// Common flag emojis for wine countries
const FLAG_EMOJIS = [
  { code: '🇮🇹', name: 'Italien' },
  { code: '🇫🇷', name: 'Frankrike' },
  { code: '🇩🇪', name: 'Tyskland' },
  { code: '🇪🇸', name: 'Spanien' },
  { code: '🇬🇷', name: 'Grekland' },
  { code: '🇨🇾', name: 'Cypern' },
  { code: '🇺🇸', name: 'USA' },
  { code: '🇦🇺', name: 'Australien' }
];

const BadgeManagementPage: React.FC<BadgeManagementPageProps> = ({
  categories = [],
  loading: categoriesLoading,
  error: categoriesError,
  onBadgeUpdated
}) => {
  const [badges, setBadges] = useState<IBadge[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingBadge, setEditingBadge] = useState<EditingBadge | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBadgeId, setSelectedBadgeId] = useState<string | null>(null);
  const [draggedBadge, setDraggedBadge] = useState<IBadge | null>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error' | 'info'}>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<{open: boolean; badgeId: string; badgeName: string}>({
    open: false,
    badgeId: '',
    badgeName: ''
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Load badges on mount
  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    setLoading(true);
    try {
      const fetchedBadges = await getBadges();
      setBadges(fetchedBadges.sort((a, b) => a.displayOrder - b.displayOrder));
    } catch (err: any) {
      showSnackbar('Kunde inte ladda märken: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBadge = () => {
    setEditingBadge({
      id: null,
      name: '',
      color: '#2196F3',
      textColor: '#FFFFFF',
      displayOrder: badges.length,
      visibleInAll: true,
      visibleInCategories: [],
      displayEmoji: '',
      showName: true,
      iconUrl: ''
    });
  };

  const handleEditBadge = (badge: IBadge) => {
    setEditingBadge({
      id: badge._id,
      name: badge.name,
      color: badge.color,
      textColor: badge.textColor,
      displayOrder: badge.displayOrder,
      visibleInAll: badge.visibleInAll !== false,
      visibleInCategories: (badge.visibleInCategories || []).map((c: any) => typeof c === 'string' ? c : c?._id).filter(Boolean),
      displayEmoji: (badge as any).displayEmoji || '',
      showName: (badge as any).showName !== false,
      iconUrl: (badge as any).iconUrl || ''
    });
    handleMenuClose();
  };

  // Wrapper function to notify updates
  const handleBadgeUpdated = () => {
    onBadgeUpdated();
    // Dispatch custom event to notify App.tsx
    window.dispatchEvent(new Event('dam:badges-updated'));
  };

  const handleSaveBadge = async () => {
    if (!editingBadge || !editingBadge.name.trim()) {
      showSnackbar('Märkesnamn krävs', 'error');
      return;
    }

    setLoading(true);

    try {
      if (editingBadge.id) {
        // Update existing badge
        const updateData: BadgeUpdateData = {
          name: editingBadge.name.trim(),
          color: editingBadge.color,
          textColor: editingBadge.textColor,
          displayOrder: editingBadge.displayOrder,
          visibleInAll: editingBadge.visibleInAll,
          visibleInCategories: editingBadge.visibleInCategories,
          // @ts-ignore
          displayEmoji: editingBadge.displayEmoji,
          // @ts-ignore
          showName: editingBadge.showName,
          // @ts-ignore
          iconUrl: (editingBadge as any).iconUrl || ''
        };
        await updateBadge(editingBadge.id, updateData);
        showSnackbar('Märke uppdaterades framgångsrikt!', 'success');
      } else {
        // Create new badge
        const createData: BadgeCreateData = {
          name: editingBadge.name.trim(),
          color: editingBadge.color,
          textColor: editingBadge.textColor,
          displayOrder: editingBadge.displayOrder,
          visibleInAll: editingBadge.visibleInAll,
          visibleInCategories: editingBadge.visibleInCategories,
          // @ts-ignore
          displayEmoji: editingBadge.displayEmoji,
          // @ts-ignore
          showName: editingBadge.showName,
          // @ts-ignore
          iconUrl: (editingBadge as any).iconUrl || ''
        };
        await createBadge(createData);
        showSnackbar('Märke skapades framgångsrikt!', 'success');
      }

      setEditingBadge(null);
      await loadBadges();
      handleBadgeUpdated();
    } catch (err: any) {
      showSnackbar('Kunde inte spara märke: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBadge = (badgeId: string, badgeName: string) => {
    setDeleteDialog({ open: true, badgeId, badgeName });
  };

  const confirmDelete = async () => {
    const { badgeId } = deleteDialog;
    setDeleteDialog({ open: false, badgeId: '', badgeName: '' });
    
    setLoading(true);
    try {
      await deleteBadge(badgeId);
      showSnackbar('Märke raderades', 'success');
      await loadBadges();
      handleBadgeUpdated();
      handleMenuClose();
    } catch (err: any) {
      showSnackbar('Kunde inte radera märke: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialog({ open: false, badgeId: '', badgeName: '' });
  };

  const handleReorderBadges = async (reorderedBadges: IBadge[]) => {
    try {
      const badgeIds = reorderedBadges.map(badge => badge._id);
      await reorderBadges(badgeIds);
      setBadges(reorderedBadges);
      handleBadgeUpdated();
    } catch (err: any) {
      showSnackbar('Kunde inte sortera om märken: ' + err.message, 'error');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, badgeId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedBadgeId(badgeId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedBadgeId(null);
  };

  const handleDragStart = (badge: IBadge) => {
    setDraggedBadge(badge);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetBadge: IBadge) => {
    e.preventDefault();
    if (!draggedBadge || draggedBadge._id === targetBadge._id) return;

    const draggedIndex = badges.findIndex(b => b._id === draggedBadge._id);
    const targetIndex = badges.findIndex(b => b._id === targetBadge._id);

    const newBadges = [...badges];
    newBadges.splice(draggedIndex, 1);
    newBadges.splice(targetIndex, 0, draggedBadge);

    // Update display order
    const updatedBadges = newBadges.map((badge, index) => ({
      ...badge,
      displayOrder: index
    }));

    setBadges(updatedBadges);
    setDraggedBadge(null);
    handleReorderBadges(updatedBadges);
  };

  const handleColorSelect = (colorOption: typeof PRESET_COLORS[0]) => {
    if (editingBadge) {
      setEditingBadge({
        ...editingBadge,
        color: colorOption.color,
        textColor: colorOption.textColor
      });
    }
    setColorPickerOpen(false);
  };

  // Render editing form - used both for new badges and inline editing
  const renderEditingForm = () => {
    if (!editingBadge) return null;

    return (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2.5, 
          mb: 2,
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9'
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {editingBadge.id ? 'Redigera märke' : 'Skapa nytt märke'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Row 1: Name, Color picker, and Preview */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label="Märkesnamn"
              value={editingBadge.name}
              onChange={(e) => setEditingBadge({ ...editingBadge, name: e.target.value })}
              placeholder="Ange märkesnamn"
              inputProps={{ maxLength: 20 }}
              helperText={`${editingBadge.name.length}/20 tecken`}
              sx={{ width: '250px' }}
              size="small"
            />

            <Box sx={{ position: 'relative' }}>
              <Button
                variant="outlined"
                startIcon={<ColorIcon />}
                onClick={() => setColorPickerOpen(!colorPickerOpen)}
                size="small"
              >
                Välj färg
              </Button>
              {colorPickerOpen && (
                <Paper elevation={3} sx={{ p: 2, mt: 1, maxHeight: 200, overflow: 'auto', position: 'absolute', zIndex: 1000, left: 0 }}>
                  <Box display="flex" flexWrap="wrap" gap={1} maxWidth="300px">
                    {PRESET_COLORS.map((colorOption) => (
                      <Tooltip key={colorOption.name} title={colorOption.name}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            backgroundColor: colorOption.color,
                            cursor: 'pointer',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            }
                          }}
                          onClick={() => handleColorSelect(colorOption)}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Paper>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">Förhandsgranskning:</Typography>
              <Chip
                label={`${editingBadge.displayEmoji || ''} ${editingBadge.showName ? (editingBadge.name || 'Märke') : ''}`.trim() || 'Märke'}
                size="small"
                style={{ backgroundColor: editingBadge.color, color: editingBadge.textColor, fontWeight: 'bold' }}
              />
            </Box>
          </Box>

          {/* Row 2: Emoji/Icon settings */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl sx={{ width: '200px' }} size="small">
              <InputLabel id="badge-emoji-label">Flagga/Emoji</InputLabel>
              <Select
                labelId="badge-emoji-label"
                value={editingBadge.displayEmoji}
                label="Flagga/Emoji"
                onChange={(e: SelectChangeEvent<string>) => setEditingBadge({ ...editingBadge, displayEmoji: e.target.value as string })}
              >
                <MenuItem value=""><em>Ingen</em></MenuItem>
                {FLAG_EMOJIS.map(opt => (
                  <MenuItem key={opt.code} value={opt.code}>{opt.code} {opt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Ikon-URL (valfritt)"
              placeholder="/uploads/flags/it.png"
              value={(editingBadge as any).iconUrl || ''}
              onChange={(e) => setEditingBadge({ ...editingBadge, iconUrl: e.target.value })}
              sx={{ width: '250px' }}
              size="small"
            />

            <FormControlLabel 
              control={<Switch checked={editingBadge.showName} onChange={(e) => setEditingBadge({ ...editingBadge, showName: e.target.checked })} size="small" />}
              label="Visa textetikett"
            />
          </Box>

          {/* Row 3: Visibility settings */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControlLabel 
              control={<Switch checked={editingBadge.visibleInAll} onChange={(e) => setEditingBadge({ ...editingBadge, visibleInAll: e.target.checked })} size="small" />}
              label="Synlig när ingen kategori är vald (Alla bilder)"
            />

            <FormControl sx={{ minWidth: '300px' }} size="small">
              <InputLabel id="badge-categories-label">Synlig i kategorier</InputLabel>
              <Select
                labelId="badge-categories-label"
                multiple
                value={editingBadge.visibleInCategories}
                label="Synlig i kategorier"
                renderValue={(selected) => selected.map(id => categories.find(c => c._id === id)?.name || id).join(', ')}
                onChange={(e: SelectChangeEvent<string[]>) => {
                  const value = e.target.value as unknown as string[];
                  setEditingBadge({ ...editingBadge, visibleInCategories: value });
                }}
              >
                {categories.map(cat => (
                  <MenuItem key={cat._id} value={cat._id}>
                    <Checkbox checked={editingBadge.visibleInCategories.indexOf(cat._id) > -1} />
                    <ListItemText primary={cat.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveBadge}
              disabled={loading || !editingBadge.name.trim()}
            >
              Spara
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => setEditingBadge(null)}
            >
              Avbryt
            </Button>
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Hantera märken (Badges)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Skapa och hantera märken för att organisera dina tillgångar.
      </Typography>

      {categoriesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {categoriesError}
        </Alert>
      )}

      {/* Add New Badge Button */}
      <Box mb={2}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleCreateBadge}
          disabled={loading || !!editingBadge}
        >
          Skapa nytt märke
        </Button>
      </Box>

      {/* Show editing form for new badges (when editingBadge.id is null) */}
      {editingBadge && !editingBadge.id && renderEditingForm()}

      {/* Badges List */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <List>
          {badges.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              Inga märken skapade ännu. Klicka på "Skapa nytt märke" för att komma igång.
            </Typography>
          ) : (
            badges.map((badge) => (
              <Box key={badge._id}>
                <ListItem
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                  draggable
                  onDragStart={() => handleDragStart(badge)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, badge)}
                >
                  <ListItemIcon>
                    <DragIcon sx={{ cursor: 'grab' }} />
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={2}>
                        <Chip label={`${(badge.iconUrl ? '' : (badge.displayEmoji || ''))} ${(badge.showName !== false) ? badge.name : ''}`.trim()}
                          size="small"
                          style={{ backgroundColor: badge.color, color: badge.textColor, fontWeight: 'bold' }}
                        />
                        <Typography variant="body1">{badge.name}</Typography>
                      </Box>
                    }
                    secondary={`Skapad: ${new Date(badge.createdAt).toLocaleDateString()}${badge.visibleInCategories && badge.visibleInCategories.length ? ` • Kategorier: ${badge.visibleInCategories.map((c: any) => typeof c === 'string' ? (categories.find(x => x._id === c)?.name || c) : c?.name).join(', ')}` : ''}`}
                  />

                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, badge._id)}
                      disabled={loading}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                
                {/* Show editing form inline when this badge is being edited */}
                <Collapse in={editingBadge?.id === badge._id} timeout="auto" unmountOnExit>
                  <Box sx={{ ml: 3, mr: 3, mb: 2 }}>
                    {editingBadge?.id === badge._id && renderEditingForm()}
                  </Box>
                </Collapse>
              </Box>
            ))
          )}
        </List>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl) && Boolean(selectedBadgeId)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            const badge = badges.find(b => b._id === selectedBadgeId);
            if (badge) handleEditBadge(badge);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Redigera märke
        </MenuItem>
        <MenuItem
          onClick={() => {
            const badge = badges.find(b => b._id === selectedBadgeId);
            if (badge && selectedBadgeId) handleDeleteBadge(selectedBadgeId, badge.name);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          Radera märke
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={cancelDelete}>
        <DialogTitle>Bekräfta radering</DialogTitle>
        <DialogContent>
          <Typography>
            Är du säker på att du vill radera märket "{deleteDialog.badgeName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Avbryt</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Radera
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BadgeManagementPage;

